/**
 * Soccer Ball Detection Interpolation Utilities
 *
 * Shared functions for predicting missing ball detections.
 * Used by both feature extraction (xgboostSoccerPredictor) and visualization.
 *
 * Prediction strategy (real-time compatible):
 * - If both frame-2 and frame-1 exist: use velocity extrapolation
 *   Predicted[frame] = Position[frame-1] + (Position[frame-1] - Position[frame-2])
 * - If only frame-1 exists: use frame-1's position (assume stationary)
 * - Returns null if frame-1 doesn't exist
 */

// Minimum confidence threshold for valid detection
export const CONFIDENCE_THRESHOLD = 0.3;

/**
 * Check if a detection has sufficient confidence
 * @param {Object|null} det - Detection object (ball)
 * @returns {boolean}
 */
export function isValidDetection(det) {
  if (!det) return false;
  return (det.confidence || 0) > CONFIDENCE_THRESHOLD;
}

/**
 * Get primary (highest confidence) ball from frame
 *
 * @param {Object} frame - Frame object
 * @returns {Object|null} ball or null if missing
 */
export function getBallDetection(frame) {
  if (!frame) return null;

  // Get balls - support both legacy (ball) and new (balls) format
  let balls = frame.balls || [];
  if (balls.length === 0 && frame.ball) {
    balls = [frame.ball];
  }

  // Sort by confidence and get highest
  if (balls.length > 0) {
    balls = [...balls].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
    return balls[0];
  }

  return null;
}

/**
 * Predict detection position using previous frames
 * - If both frame-2 and frame-1 exist: use velocity extrapolation
 *   Predicted[frame] = 2 * Position[frame-1] - Position[frame-2]
 * - If only frame-1 exists: use frame-1's position (assume stationary)
 * - If neither exists: return null
 *
 * @param {Object|null} detMinus2 - Detection at frame-2
 * @param {Object|null} detMinus1 - Detection at frame-1
 * @returns {Object|null} Predicted detection or null if not enough data
 */
export function predictDetection(detMinus2, detMinus1) {
  const validMinus2 = isValidDetection(detMinus2);
  const validMinus1 = isValidDetection(detMinus1);

  // If frame-1 is not valid, we can't predict
  if (!validMinus1) {
    return null;
  }

  // If only frame-1 is valid, use its position (assume stationary)
  if (!validMinus2) {
    return {
      x: detMinus1.x,
      y: detMinus1.y,
      width: detMinus1.width,
      height: detMinus1.height,
      confidence: detMinus1.confidence || 0,
      predicted: true
    };
  }

  // Both frames valid: use velocity extrapolation
  // Predicted position = frame-1 + velocity = 2*frame-1 - frame-2
  return {
    x: 2 * detMinus1.x - detMinus2.x,
    y: 2 * detMinus1.y - detMinus2.y,
    width: detMinus1.width || detMinus2.width,
    height: detMinus1.height || detMinus2.height,
    confidence: Math.min(detMinus1.confidence || 0, detMinus2.confidence || 0),
    predicted: true
  };
}

// Maximum chain depth for predictions (how many frames back we try to find valid detections)
// This affects both recursion depth and history buffer size in streaming mode
export const MAX_CHAIN_DEPTH = 10;

// Default max ancestor depth - stop predicting after this many consecutive missing frames
export const DEFAULT_MAX_ANCESTOR_DEPTH = 5;

/**
 * Streaming predictor for sequential frame processing.
 * Efficient O(1) per frame, stores only t-1 and t-2 state.
 */
export class StreamingDetectionPredictor {
  /**
   * @param {number} maxAncestorDepth - Stop predicting after this many consecutive missing frames
   */
  constructor(maxAncestorDepth = DEFAULT_MAX_ANCESTOR_DEPTH) {
    this.maxAncestorDepth = maxAncestorDepth;
    this.reset();
  }

  /**
   * Reset state (call when starting new sequence)
   */
  reset() {
    // State for t-1 frame
    this.prevBall = null;      // { detection, ancestorDepth }
    // State for t-2 frame
    this.prevPrevBall = null;
  }

  /**
   * Process next frame in sequence.
   * Call this for each frame in order (0, 1, 2, 3, ...).
   *
   * @param {Object} frame - Frame data with ball detections
   * @returns {Object} { ball, ballAncestorDepth, ballWasPredicted }
   */
  processFrame(frame) {
    const rawBall = getBallDetection(frame);

    // Process ball
    const ballResult = this._processDetection(
      rawBall,
      this.prevBall,
      this.prevPrevBall
    );

    // Shift state: t-2 = t-1, t-1 = current
    this.prevPrevBall = this.prevBall;
    this.prevBall = { detection: ballResult.detection, ancestorDepth: ballResult.ancestorDepth };

    return {
      ball: ballResult.detection,
      ballAncestorDepth: ballResult.ancestorDepth,
      ballWasPredicted: ballResult.wasPredicted
    };
  }

  /**
   * Get current state for velocity calculation (t-1 predictions)
   * @returns {Object} { ball } - Previous frame's prediction
   */
  getPrevState() {
    return {
      ball: this.prevBall?.detection || null
    };
  }

  /**
   * Get t-2 state for acceleration calculation
   * @returns {Object} { ball } - Frame before previous prediction
   */
  getPrevPrevState() {
    return {
      ball: this.prevPrevBall?.detection || null
    };
  }

  _processDetection(rawDetection, prev, prevPrev) {
    // If raw detection is valid, use it directly
    if (isValidDetection(rawDetection)) {
      return {
        detection: rawDetection,
        ancestorDepth: 0,
        wasPredicted: false
      };
    }

    // No previous data to predict from
    if (!prev || prev.detection === null) {
      return {
        detection: null,
        ancestorDepth: Infinity,
        wasPredicted: false
      };
    }

    // Check if we've exceeded max ancestor depth
    const prevDepth = prev.ancestorDepth;
    const prevPrevDepth = prevPrev?.ancestorDepth ?? 0;
    const newDepth = Math.max(prevDepth, prevPrevDepth) + 1;

    if (newDepth > this.maxAncestorDepth) {
      return {
        detection: null,
        ancestorDepth: Infinity,
        wasPredicted: false
      };
    }

    // Predict using velocity extrapolation
    const predicted = predictDetection(
      prevPrev?.detection || null,
      prev.detection
    );

    if (predicted === null) {
      return {
        detection: null,
        ancestorDepth: Infinity,
        wasPredicted: false
      };
    }

    return {
      detection: predicted,
      ancestorDepth: newDepth,
      wasPredicted: true
    };
  }
}

/**
 * Get detection at arbitrary frame using recursive cache.
 * Use when jumping to random frames (visualizer navigation, batch processing start).
 *
 * @param {Array} frames - All frames array
 * @param {number} frameIdx - Target frame index
 * @param {Object} options - { maxAncestorDepth, cache }
 * @returns {Object} { detection, ancestorDepth, wasPredicted }
 */
export function getDetectionAtFrame(frames, frameIdx, options = {}) {
  const maxAncestorDepth = options.maxAncestorDepth ?? DEFAULT_MAX_ANCESTOR_DEPTH;
  const cache = options.cache ?? {};

  const cacheKey = `${frameIdx}`;

  // Check cache first
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  // Base case: out of bounds
  if (frameIdx < 0 || frameIdx >= frames.length) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Check raw detection
  const raw = getBallDetection(frames[frameIdx]);

  if (isValidDetection(raw)) {
    const result = { detection: raw, ancestorDepth: 0, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  if (frameIdx <= 0) {
    // Can't look back further
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Recursive: get t-1 and t-2
  const prev1 = getDetectionAtFrame(frames, frameIdx - 1, { maxAncestorDepth, cache });
  const prev2 = getDetectionAtFrame(frames, frameIdx - 2, { maxAncestorDepth, cache });

  // If t-1 has no detection, can't predict
  if (prev1.detection === null) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Calculate new ancestor depth
  const newDepth = Math.max(prev1.ancestorDepth, prev2.ancestorDepth ?? 0) + 1;

  if (newDepth > maxAncestorDepth) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  // Predict using velocity extrapolation
  const predicted = predictDetection(prev2.detection, prev1.detection);

  if (predicted === null) {
    const result = { detection: null, ancestorDepth: Infinity, wasPredicted: false };
    cache[cacheKey] = result;
    return result;
  }

  const result = { detection: predicted, ancestorDepth: newDepth, wasPredicted: true };
  cache[cacheKey] = result;
  return result;
}

/**
 * Build predictions for a range of frames (for batch processing or visualization).
 * Processes sequentially for efficiency, with optional start cache for random access.
 *
 * @param {Array} frames - All frames array
 * @param {number} startIdx - Start frame index
 * @param {number} endIdx - End frame index (inclusive)
 * @param {Object} options - { maxAncestorDepth }
 * @returns {Array} Array of { ball, ballAncestorDepth, ballWasPredicted } for each frame
 */
export function buildPredictionsForRange(frames, startIdx, endIdx, options = {}) {
  const maxAncestorDepth = options.maxAncestorDepth ?? DEFAULT_MAX_ANCESTOR_DEPTH;
  const results = [];

  // If starting from frame 0, use pure sequential
  if (startIdx === 0) {
    const predictor = new StreamingDetectionPredictor(maxAncestorDepth);
    for (let i = startIdx; i <= endIdx && i < frames.length; i++) {
      results.push(predictor.processFrame(frames[i]));
    }
    return results;
  }

  // Starting from arbitrary frame - use recursive cache to bootstrap
  const cache = {};

  // Get initial state using recursive lookback
  const initBall1 = getDetectionAtFrame(frames, startIdx - 1, { maxAncestorDepth, cache });
  const initBall2 = getDetectionAtFrame(frames, startIdx - 2, { maxAncestorDepth, cache });

  // Create predictor with initialized state
  const predictor = new StreamingDetectionPredictor(maxAncestorDepth);
  predictor.prevBall = { detection: initBall1.detection, ancestorDepth: initBall1.ancestorDepth };
  predictor.prevPrevBall = { detection: initBall2.detection, ancestorDepth: initBall2.ancestorDepth };

  // Process range sequentially
  for (let i = startIdx; i <= endIdx && i < frames.length; i++) {
    results.push(predictor.processFrame(frames[i]));
  }

  return results;
}
